import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Button, Fade } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const COLORS = [
  '#1976d2', '#d32f2f', '#2e7d32', '#ed6c02', '#9c27b0',
  '#00838f', '#c62828', '#558b2f', '#e65100', '#6a1b9a',
  '#00695c', '#ad1457', '#33691e', '#bf360c', '#4527a0',
];

const formatCurrency = (value) =>
  `$${Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CustomTooltip = ({ active, payload, isDrilldown }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0];
  const entry = data.payload;
  return (
    <Box sx={{ bgcolor: 'background.paper', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="body2" fontWeight={600}>{data.name}</Typography>
      <Typography variant="body2">
        Avg/mo: {formatCurrency(data.value)}
      </Typography>
      {entry.totalSpending != null && (
        <Typography variant="body2" color="text.secondary">
          Total: {formatCurrency(entry.totalSpending)}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {((entry.percent || 0) * 100).toFixed(1)}% of total
      </Typography>
      {!isDrilldown && (
        <Typography variant="caption" display="block" color="primary.main" sx={{ mt: 0.5 }}>
          Click to see subcategories
        </Typography>
      )}
    </Box>
  );
};

const renderLegendText = (value) => (
  <span style={{ fontSize: '0.8rem', color: '#555' }}>{value}</span>
);

function buildChartSlices(items, limit = 10) {
  const sorted = [...items].sort((a, b) => b.value - a.value);
  if (sorted.length <= limit) return sorted;
  const top = sorted.slice(0, limit - 1);
  const rest = sorted.slice(limit - 1);
  const otherAvg = rest.reduce((sum, c) => sum + c.value, 0);
  const otherTotal = rest.reduce((sum, c) => sum + (c.totalSpending || 0), 0);
  return [
    ...top,
    { name: 'Other', value: otherAvg, totalSpending: otherTotal, _drilldown: null },
  ];
}

export default function CategoryPieChart({ data, drilldownData, title = 'Monthly Average Spending by Category' }) {
  const [selectedParent, setSelectedParent] = useState(null);

  const hasDrilldown = drilldownData && drilldownData.length > 0;

  // Build top-level chart data using monthly averages
  let topLevelData;
  if (hasDrilldown) {
    topLevelData = buildChartSlices(
      drilldownData.map((c) => ({
        name: c.parent_category,
        value: c.average_monthly,
        totalSpending: c.total,
        _drilldown: c,
      }))
    );
  } else {
    // Fallback to flat category data
    topLevelData = buildChartSlices(
      (data || []).map((c) => ({
        name: c.category,
        value: c.average_monthly || c.total,
        totalSpending: c.total,
      }))
    );
  }

  // Build subcategory chart data when drilled down
  let subcategoryData = [];
  if (selectedParent && selectedParent._drilldown) {
    const subs = selectedParent._drilldown.subcategories || [];
    subcategoryData = buildChartSlices(
      subs.map((c) => ({
        name: c.category,
        value: c.average_monthly,
        totalSpending: c.total,
      }))
    );
  }

  const isShowingSubs = selectedParent !== null;
  const chartData = isShowingSubs ? subcategoryData : topLevelData;

  // Add percent to each entry
  const grandTotal = chartData.reduce((sum, d) => sum + d.value, 0);
  const chartDataWithPercent = chartData.map((d) => ({
    ...d,
    percent: grandTotal ? d.value / grandTotal : 0,
  }));

  const handleSliceClick = (entry) => {
    if (!isShowingSubs && entry._drilldown && entry._drilldown.subcategories?.length > 0) {
      setSelectedParent(entry);
    }
  };

  const handleBack = () => {
    setSelectedParent(null);
  };

  const chartTitle = isShowingSubs
    ? `${selectedParent.name} — Subcategories`
    : title;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {isShowingSubs && (
            <Fade in>
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
            </Fade>
          )}
          <Typography variant="h6">{chartTitle}</Typography>
        </Box>
        {isShowingSubs && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Monthly Avg: {formatCurrency(selectedParent.value)} — Total: {formatCurrency(selectedParent.totalSpending)}
          </Typography>
        )}
        {!isShowingSubs && hasDrilldown && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Click a category to see its subcategories
          </Typography>
        )}
        {chartDataWithPercent.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No spending data available.
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={420}>
            <PieChart>
              <Pie
                data={chartDataWithPercent}
                cx="50%"
                cy="40%"
                innerRadius={70}
                outerRadius={130}
                paddingAngle={2}
                dataKey="value"
                onClick={(_, index) => handleSliceClick(chartDataWithPercent[index])}
                style={!isShowingSubs && hasDrilldown ? { cursor: 'pointer' } : {}}
              >
                {chartDataWithPercent.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip isDrilldown={isShowingSubs || !hasDrilldown} />} />
              <Legend
                layout="horizontal"
                align="center"
                verticalAlign="bottom"
                formatter={renderLegendText}
                wrapperStyle={{ paddingTop: 12 }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
